import { Layer } from "@nivo/line";
import { MouseEvent, ReactElement, useState } from "react";
import { YAxisLegend } from "src/components/monitoring/axisLegends/YAxisLegend";
import { LineBottomAxis } from "src/components/monitoring/axisLegends/LineBottomAxis";
import { ChartGrid } from "src/components/monitoring/axisLegends/ChartGrid";
import { DataSeriesUnit, TimeFrame, AxisConfig } from "src/components/monitoring/commonTypes";
import { getLabelsForYAxis } from "src/components/monitoring/axisUtils";
import { getHighestValueBetweenTimeFrameForUnit } from "src/components/monitoring/chartUtils";
import { createCustomLineLayers } from "src/components/monitoring/ChartLines";
import {
    ChartItemData,
    LineChartItem,
    LineChartLevel,
    SelectedTimeValues,
} from "src/components/monitoring/senec-line-chart/lineChartTypes";
import { Point } from "@nivo/core";
import { StatusLine } from "src/components/monitoring/senec-line-chart/StatusLine";
import { MonitoringLine } from "src/components/monitoring/senec-line-chart/SenecLineChartMonitoringLine";

export type SenecLineChartProps = {
    /** The data points to be plotted on the line chart */
    data: LineChartItem[];
    /** @deprecated Whether to display the left Y-axis.  Use leftAxisConfig for left axis config */
    showLeftAxis?: boolean;
    /** @deprecated Whether to display the right Y-axis. Use rightAxisConfig for right axis config */
    showRightAxis?: boolean;
    /** Left axis config */
    leftAxisConfig?: AxisConfig;
    /** Right axis config */
    rightAxisConfig?: AxisConfig;
    /** Amount of horizontal lines in the Chart. @default is 6 */
    chartLines?: number;
    /** Timeframe for which the chart data is rendered. Can be smaller or bigger than the actual data. */
    timeframe: TimeFrame;
    /** Types of chart legends to be selected. Not selected types are filtered out. */
    selectedLegendTypes: string[];
    /** Localization setting for the chart. Relevant for translating numbers. */
    locale: Intl.Locale;
    /** The number of labels to display on the X-axis */
    numberOfXLabels: 4 | 5 | 7 | 12 | 13;
    /** Optional property to display selected time */
    showSelectedTime?: boolean;
    /** Optional property defining if tooltip should be shown */
    showTooltip?: boolean;
    /** Optional property to define custom day tooltip format */
    customTooltipDateFormat?: Intl.DateTimeFormatOptions;
    /** Optional callback function to get values when a time is selected */
    onSelectedTimeValues?: (selectedTimeValues: SelectedTimeValues) => void;
    /** Optional callback when chart is clicked */
    onClick?: (point: Point, event: MouseEvent) => void;
    /** Optional function to map values for a better tooltip representation */
    tooltipValueMapper?: (value: number | string, id: number | string) => string;
    /** Optional property to set style of line Chart curve. Default is 'monotoneX' */
    curve?: "linear" | "monotoneX" | "monotoneY" | "natural" | "step" | "stepBefore" | "stepAfter";
};

/**
 * The `SenecLineChart` is a visual representation of energy metrics over a time period from 1 hour to 1 day. The chart displays energy values in kW or W along the Y-axis and time (in minutes or hours) along the X-axis. Further, it can show percent values on the other Y-axis. It automatically calculates the highest energy value to adjust the height of the chart. It can be used on desktop and mobile.
 */
export const SenecLineChart = ({
    data,
    showLeftAxis,
    showRightAxis,
    leftAxisConfig,
    rightAxisConfig,
    chartLines = 6,
    timeframe,
    selectedLegendTypes,
    locale,
    numberOfXLabels,
    showSelectedTime = false,
    showTooltip = false,
    customTooltipDateFormat,
    onSelectedTimeValues,
    onClick,
    tooltipValueMapper = undefined,
    curve = "monotoneX",
}: SenecLineChartProps): ReactElement => {
    const stateData = data
        .filter((item) => item.unit === DataSeriesUnit.STATE)
        .filter((line) => selectedLegendTypes.includes(line.id));
    const selectedData = data
        .filter((item) => item.unit !== DataSeriesUnit.STATE)
        .filter((line) => selectedLegendTypes.includes(line.id));
    addGradientToData(selectedData);
    const [selectedDate, setSelectedDate] = useState<Date>();

    const wrappedCallback = (value: SelectedTimeValues) => {
        setSelectedDate(new Date(value.selectedTime));
        if (onSelectedTimeValues) {
            onSelectedTimeValues(value);
        }
    };

    const leftAxisHighestValue = getHighestValueBetweenTimeFrameForUnit(selectedData, timeframe, leftAxisConfig?.unit);
    const leftYLegendValues = getLabelsForYAxis(
        leftAxisHighestValue.highestLabelValue == 0 ? 1 : leftAxisHighestValue.highestLabelValue,
        locale.toString(),
        chartLines,
    );

    const rightAxisHighestValue = getHighestValueBetweenTimeFrameForUnit(
        selectedData,
        timeframe,
        showRightAxis ? DataSeriesUnit.PERCENT : rightAxisConfig?.unit,
    );
    const rightYLegendValues = getLabelsForYAxis(rightAxisHighestValue.highestLabelValue, locale.toString(), chartLines);

    const levels: LineChartLevel[] = [LineChartLevel.BACK, LineChartLevel.MIDDLE, LineChartLevel.FRONT];

    const renderedLines = levels.map((level) => {
        const { layers, maxYValuePerLevel } = getLevelBasedProperties(
            selectedData,
            level,
            curve,
            leftAxisHighestValue.highestDataValue,
            rightAxisHighestValue.highestDataValue,
            leftAxisConfig,
            rightAxisConfig,
        );
        return (
            <MonitoringLine
                key={level}
                data={data}
                layers={[...layers, "slices", "crosshair"]}
                locale={locale}
                timeframe={timeframe}
                selectedLegendTypes={selectedLegendTypes}
                maxYValue={maxYValuePerLevel}
                colors={data.map((item) => item.color)}
                showSelectedTime={showSelectedTime}
                showTooltip={showTooltip}
                customTooltipDateFormat={customTooltipDateFormat}
                onSelectedTimeValues={wrappedCallback}
                onClick={onClick}
                tooltipValueMapper={tooltipValueMapper}
            />
        );
    });

    return (
        <>
            {renderedLines}
            {showLeftAxis && !leftAxisConfig && (
                <YAxisLegend
                    variant={"left"}
                    chartType={"line"}
                    yLegendValues={leftYLegendValues}
                    unit={leftAxisHighestValue.unit}
                />
            )}
            {leftAxisConfig && (
                <YAxisLegend variant={"left"} chartType={"line"} yLegendValues={leftYLegendValues} unit={leftAxisConfig.unit} />
            )}
            {showRightAxis && (
                <YAxisLegend
                    variant={"right"}
                    chartType={"line"}
                    yLegendValues={rightYLegendValues}
                    unit={rightAxisHighestValue.unit}
                />
            )}
            {rightAxisConfig && (
                <YAxisLegend
                    variant={"right"}
                    chartType={"line"}
                    yLegendValues={rightAxisHighestValue.highestLabelValue ? (rightAxisConfig.ticks ?? rightYLegendValues) : []}
                    unit={rightAxisConfig.unit}
                />
            )}
            <ChartGrid amountLines={chartLines} />
            {stateData.length > 0 && <StatusLine timeframe={timeframe} series={stateData[0]} selectedDate={selectedDate} />}
            <LineBottomAxis
                timeframe={timeframe}
                locale={locale}
                numberOfXLabels={numberOfXLabels}
                largerGap={stateData.length > 0}
            />
        </>
    );
};

/**
 * Returns the layers and the maximum Y value for a specific level. When new leftAxisConfig or rightAxisConfig is provided, the maximum value is taken from the config or the highest value of the data. When both are not provided the old logic is used in which the left axis is the first found unit and the right axis is always PERCENT.
 */
const getLevelBasedProperties = (
    data: LineChartItem[],
    level: LineChartLevel,
    curve: "linear" | "monotoneX" | "monotoneY" | "natural" | "step" | "stepBefore" | "stepAfter",
    leftHighestValue: number,
    rightHighestValue: number,
    leftAxisConfig?: AxisConfig,
    rightAxisConfig?: AxisConfig,
) => {
    const levelItems = data.filter((lineChartItem) => lineChartItem.level === level);
    if (levelItems.length === 0) return { layers: [], maxYValuePerLevel: 0 };

    const firstUnit = levelItems[0].unit;
    if (!levelItems.every((item) => item.unit === firstUnit)) {
        throw new Error(`All items for level ${level} should have the same unit.`);
    }

    let maxValue: number = leftHighestValue;

    if (leftAxisConfig && firstUnit === leftAxisConfig.unit) {
        maxValue = leftAxisConfig.maxValue ?? leftHighestValue;
    }
    if (rightAxisConfig && firstUnit === rightAxisConfig.unit) {
        maxValue = rightAxisConfig.maxValue ?? rightHighestValue;
    }

    if (firstUnit === DataSeriesUnit.PERCENT && !rightAxisConfig && !leftAxisConfig) {
        maxValue = rightHighestValue;
    }

    const layers: Layer[] = createCustomLineLayers(levelItems, curve);

    return { layers, maxYValuePerLevel: maxValue };
};

const addGradientToData = (selectedData: LineChartItem[]) => {
    selectedData.forEach((lineChartItem) => {
        if (lineChartItem.gradient) {
            lineChartItem.data.forEach((chartItemData: ChartItemData) => {
                chartItemData.fill = `url(#gradient-${lineChartItem.id})`;
                chartItemData.hasArea = true;
            });
        }
    });
};
